import { useQuery } from '@tanstack/react-query';
import { useParams, useNavigate } from '@tanstack/react-router';
import { Icon } from '../components/Icon.tsx';
import { requestsApi, type RequestStatus } from '../features/requests/api.ts';
import { InvitesPanel } from '../components/InvitesPanel.tsx';
import { ComparisonTable } from '../components/ComparisonTable.tsx';
import { ManualQuotePanel } from '../components/ManualQuotePanel.tsx';
import { ModeChooser } from '../components/ModeChooser.tsx';
import { SpecDocBox } from '../components/SpecDocBox.tsx';
import { CountdownBadge } from '../components/CountdownBadge.tsx';
import { t } from '../lib/strings.ts';
import { formatDate } from '@/lib/format';

function statusPill(status: RequestStatus) {
  switch (status) {
    case 'DRAFT': return 'pill pill-neutral';
    case 'SENT': return 'pill pill-pending';
    case 'CLOSED': return 'pill pill-success';
  }
}

function statusLabel(status: RequestStatus) {
  switch (status) {
    case 'DRAFT': return t.requestDetail.statusDraft;
    case 'SENT': return t.requestDetail.statusSent;
    case 'CLOSED': return t.requestDetail.statusClosed;
  }
}

export function RequestDetail(): JSX.Element {
  const { requestId } = useParams({ from: '/requests/$requestId' });
  const navigate = useNavigate();

  const { data: request, isLoading } = useQuery({
    queryKey: ['request', requestId],
    queryFn: () => requestsApi.get(requestId),
  });

  if (isLoading) {
    return (
      <div className="page">
        <p className="status-label text-muted-foreground" role="status">{t.requestDetail.loading}</p>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="page">
        <p className="text-sm text-destructive">{t.requestDetail.notFound}</p>
      </div>
    );
  }

  return (
    <div className="page space-y-6">
      {/* Header */}
      <header className="flex flex-wrap items-start gap-4">
        <button
          onClick={() => void navigate({ to: '/requests' })}
          className="btn-ghost btn-sm mt-0.5"
          aria-label={t.requestDetail.ariaBack}
        >
          <Icon name="arrow_forward" size={15} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-3 mb-1">
            <span className="mono-meta text-muted-foreground">{request.refNumber}</span>
            <span className={statusPill(request.status)}>
              {statusLabel(request.status)}
            </span>
          </div>
          <h1 className="text-2xl font-bold text-ink leading-tight">{request.title}</h1>
          <div className="mt-1 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
            <span>{t.requestDetail.createdAt} <span className="num">{formatDate(request.createdAt)}</span></span>
            {request.deadline && (
              <span>{t.requestDetail.deadlineLabel}: <span className="num">{formatDate(request.deadline)}</span></span>
            )}
            {request.status === 'SENT' && <CountdownBadge deadline={request.deadline} />}
          </div>
        </div>
      </header>

      {/* Items table */}
      <section className="space-y-2">
        <div className="section-heading">
          <h2 className="status-label text-muted-foreground">{t.requestDetail.itemsHeading} (<span className="num">{request.items.length}</span>)</h2>
        </div>
        {/* Mobile: stacked cards */}
        <ul className="space-y-2 md:hidden">
          {request.items.map((item, idx) => (
            <li key={item.id} className="card-paper p-3.5">
              <div className="flex items-start justify-between gap-3">
                <p className="font-medium leading-snug">{item.name}</p>
                <span className="mono-meta text-muted-foreground shrink-0 num">#{idx + 1}</span>
              </div>
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                <span><span className="tabular text-foreground num">{item.qty}</span> {item.unit}</span>
                {item.spec && <span>{item.spec}</span>}
              </div>
            </li>
          ))}
        </ul>

        {/* Desktop: table */}
        <div className="card-paper hidden overflow-x-auto md:block">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-muted-foreground">
                <th className="px-4 py-3 text-start font-medium">{t.requestDetail.colNum}</th>
                <th className="px-4 py-3 text-start font-medium">{t.requestDetail.colItem}</th>
                <th className="px-4 py-3 text-start font-medium">{t.requestDetail.colQty}</th>
                <th className="px-4 py-3 text-start font-medium">{t.requestDetail.colUnit}</th>
                <th className="px-4 py-3 text-start font-medium">{t.requestDetail.colSpec}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {request.items.map((item, idx) => (
                <tr key={item.id} className="hover:bg-muted/40 transition-colors">
                  <td className="px-4 py-3 mono-meta text-muted-foreground num">{idx + 1}</td>
                  <td className="px-4 py-3 font-medium">{item.name}</td>
                  <td className="px-4 py-3 tabular num">{item.qty}</td>
                  <td className="px-4 py-3 text-muted-foreground">{item.unit}</td>
                  <td className="px-4 py-3 text-muted-foreground">{item.spec ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* ── Mode chooser — only while a fresh draft, for procurement staff ───── */}
      <ModeChooser requestId={request.id} mode={request.mode} status={request.status} />

      {request.mode === 'MANUAL' ? (
        /* ── Manual quotation flow ──────────────────────────────────────────── */
        <section id="manual-panel" aria-label={t.manualQuote.heading}>
          <ManualQuotePanel requestId={request.id} />
        </section>
      ) : (
        <>
          {/* ── Optional specification document ──────────────────────────────── */}
          <SpecDocBox requestId={request.id} />

          {/* ── Vendor Invitations panel ─────────────────────────────────────── */}
          <section id="invites-panel" aria-label={t.requestDetail.ariaVendorInvitations}>
            <InvitesPanel requestId={request.id} deadline={request.deadline} />
          </section>

          {/* ── Quote Comparison + Award panel ───────────────────────────────── */}
          {request.status !== 'DRAFT' && (
            <section id="comparison-panel" aria-label={t.requestDetail.ariaQuoteComparison}>
              <ComparisonTable requestId={request.id} />
            </section>
          )}
        </>
      )}
    </div>
  );
}
